import './UsbSettings.scss'
import React, {useEffect, useState} from "react";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import {DatePicker, Input, InputGroup, Radio, RadioGroup, toaster, Whisper} from "rsuite";
import UpdateButton from "../../../../shared/UpdateButton/UpdateButton";
import {UsbApi} from "../../../../../services/Usb.api";
import NotificationFactory from "../../../../../shared/Notifications";
import moment from "moment";
import {getTooltip} from "../../../../../shared/Tooltip";
import {UsbProps} from "../../../../../models/Usb.model";
import {Usb_UsbPowerSchedulerMode, Usb_UsbSettingsModel} from "../../../../../api";
import {BsBootstrapRebootIcon, FaClockIcon, TfiSaveIcon} from "../../../../../models/Icons.model";
import RebootConfirmationModal from "../../../ExternalDevices/components/Dimmer/components/RebootConfirmationModal";
import {mapTimeValueToTimeString} from "../../../../../shared/Helpers.utils";

const UsbSettings = ({deviceId}: UsbProps) => {
  const [usbParams, setUsbParams] = useState<Usb_UsbSettingsModel | null>(null);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enableTime, setEnableTime] = useState<Date | null>(null);
  const [disableTime, setDisableTime] = useState<Date | null>(null);
  const [usbMode, setUsbMode] = useState<Usb_UsbPowerSchedulerMode>('AlwaysOff');
  const [seconds, setSeconds] = useState<number>(10);

  useEffect(() => {
    if (deviceId !== null && deviceId !== undefined) {
      getUsbParams().then();
    }
  }, [deviceId]);


  const getUsbParams = async () => {
    setLoading(true);
    try {
      const result = await UsbApi.getUsbParams(deviceId);
      setUsbParams(result);

      setUsbMode(result.mode || 'AlwaysOff');

      if (result.enableTime) {
        const enableDate = moment(mapTimeValueToTimeString(result.enableTime), 'HH:mm:ss').toDate();
        setEnableTime(enableDate ?? null);
      }

      if (result.disableTime) {
        const disableDate = moment(mapTimeValueToTimeString(result.disableTime), 'HH:mm:ss').toDate();
        setDisableTime(disableDate);
      }

    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const onSavePhoneNumber = () => {

  }

  const handleOpenRebootModal = () => {
    setShowRebootModal(true);
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleConfirmReboot = async () => {
    setShowRebootModal(false);
    try {
      const result = await UsbApi.resetUsb(deviceId, seconds)

      if (result) {
        toaster.push(
          NotificationFactory.successReboot(),
          {placement: "topEnd", duration: 3000}
        );
        getUsbParams().then();
      } else {
        toaster.push(
          NotificationFactory.errorReboot(),
          {placement: "topEnd", duration: 3000}
        );
        console.error("Ошибка при перезапуске устройства");
      }

    } catch (error) {
      toaster.push(
        NotificationFactory.errorReboot(),
        {placement: "topEnd", duration: 3000}
      );
      console.error("Ошибка при перезапуске устройства:", error);
    }
  };

  const updateEnableTime = async () => {
    if (!enableTime) {
      toaster.push(
        NotificationFactory.error("Время включения не установлено"),
        {placement: "topEnd"}
      );
      return;
    }

    setLoading(true);
    try {
      const timeString = {
        time: moment(enableTime).format('HH:mm:00')
      };

      const result = await UsbApi.updateEnableTime(deviceId, timeString);

      if (result) {
        getUsbParams().then();
        toaster.push(
          NotificationFactory.success("Время включения обновлено"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка обновления времени включения"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const updateDisableTime = async () => {
    if (!disableTime) {
      toaster.push(
        NotificationFactory.error("Время выключения не установлено"),
        {placement: "topEnd"}
      );
      return;
    }

    setLoading(true);
    try {
      const timeString = {
        time: moment(disableTime).format('HH:mm:00')
      };

      const result = await UsbApi.updateDisableTime(deviceId, timeString);

      if (result) {
        getUsbParams().then();
        toaster.push(
          NotificationFactory.success("Время выключения обновлено"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка обновления времени выключения"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const updateUsbMode = async (mode: Usb_UsbPowerSchedulerMode) => {
    setLoading(true);
    try {
      const result = await UsbApi.changeUsbMode(deviceId, mode);

      if (result) {
        setUsbMode(mode);
        toaster.push(
          NotificationFactory.success("Режим работы обновлен"),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка обновления режима работы"),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (value: string) => {
    const mode = value as Usb_UsbPowerSchedulerMode;
    setUsbMode(mode);
    updateUsbMode(mode).then();
  };

  const handleSecondsChange = (value: number) => {
    setSeconds(value ?? 0);
  };

  return (
    <>
      <div className="fieldset-wrapper mb-4">
        <div className="legend-title main-legend-title">Настройка USB</div>
        <section className="primary-clock">
          <div className="row">
            <div className="col-md-12 text-end">
              <UpdateButton loading={loading} onRefresh={getUsbParams}/>
            </div>
          </div>
          <div className="row text-center align-items-center p-1">
            <div className="col-md-1">
              <StatusControl status={usbParams?.enabled ? "online" : "offline"}></StatusControl>
            </div>
            <div className="col-md-7">
              <RadioGroup
                name="radio-group-inline"
                inline
                value={usbMode}
                onChange={handleModeChange as any}
                disabled={loading}>
                <div className="me-1">
                  <Radio value="AlwaysOff">Всегда выключен</Radio>
                </div>
                <div>
                  <Radio value="AlwaysOn" className="me-1">Всегда включен</Radio>
                </div>
                <div>
                  <Radio value="Schedule" className="me-2">Подключено по времени</Radio>
                </div>
              </RadioGroup>
            </div>
            <div className="col-md-4">
              <InputGroup>
                <InputGroup.Addon>Модем</InputGroup.Addon>
                <Input
                  placeholder="+70000000000"
                  type="text"
                />
                <InputGroup.Addon style={{cursor: 'pointer'}} onClick={onSavePhoneNumber}>
                  <TfiSaveIcon/>
                </InputGroup.Addon>
              </InputGroup>
            </div>
          </div>
          <div className="row text-center align-items-center p-1">
            <div className="col-md-3 mt-1">
              <InputGroup>
                <InputGroup.Addon style={{width: 50}}>Вкл</InputGroup.Addon>
                <DatePicker
                  format="HH:mm"
                  caretAs={FaClockIcon}
                  loading={loading}
                  value={enableTime ?? null}
                  onChange={setEnableTime}
                  cleanable={false}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon onClick={updateEnableTime}/>
                </InputGroup.Addon>
              </InputGroup>
            </div>
            <div className="col-md-3 mt-1">
              <InputGroup>
                <InputGroup.Addon style={{width: 50}}>Выкл</InputGroup.Addon>
                <DatePicker
                  format="HH:mm"
                  caretAs={FaClockIcon}
                  loading={loading}
                  value={disableTime}
                  onChange={setDisableTime}
                  cleanable={false}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon onClick={updateDisableTime}/>
                </InputGroup.Addon>
              </InputGroup>
            </div>
            <div className="col-md-4 offset-lg-2 offset-md-2  text-center mt-1">
              <InputGroup>
                <InputGroup.Addon>Перезапустить</InputGroup.Addon>
                <Input
                  value={seconds}
                  type="number"
                  min={0}
                  max={3600}
                  step={1}
                  onChange={handleSecondsChange as any}>
                </Input>
                <InputGroup.Addon>Сек</InputGroup.Addon>
                <Whisper
                  placement="top"
                  controlId="control-id-hover"
                  trigger="hover"
                  speaker={getTooltip('Перезапустить устройство')}>
                <InputGroup.Addon style={{cursor: 'pointer'}} onClick={handleOpenRebootModal}>
                  <BsBootstrapRebootIcon style={{color:'red'}}/>
                </InputGroup.Addon>
                </Whisper>
              </InputGroup>
            </div>
          </div>
        </section>
      </div>

      <RebootConfirmationModal
        show={showRebootModal}
        onHide={handleCloseRebootModal}
        onConfirm={handleConfirmReboot}
      />

    </>
  )
}

export default UsbSettings;
